// eslint-disable-next-line import/extensions
import "verdi-common/extensions/string.extension";

import { DeviceTypeConfigurations, type ThirdPartyDeviceConfiguration, type ThirdPartyDeviceType } from "verdi-common";
import { VertexInfo } from "verdi-common/schemas/commonSubSchemas/vertexInfo";
import type { SensorSpecifier } from "verdi-common/schemas/device/subSchemas/sensorSpecifier";
import { ThirdPartyDevice } from "verdi-common/schemas/thirdPartyDevice/thirdPartyDevice";
import * as APICommands from "../../APICommands/index.js";
import { SessionHandler } from "../../APICommands/index.js";
import { findEventually } from "../AMasterIndex.js";
import type { Zone } from "../Zone.js";
import { DeviceBase } from "./DeviceBase.js";
import { IrrigationDeviceSensor } from "./IrrigationDeviceSensor.js";
import { DeviceWarning } from "./Warnings.js";

export class ThirdPartyDeviceBase extends DeviceBase {
    connectedSensors: (IrrigationDeviceSensor | undefined)[] = [];
    connectedZoneIDs: string[] = [];
    connectedZones: Zone[] = [];
    EnclosureID: string;
    deviceType: ThirdPartyDeviceType;
    category = "thirdPartyDevice";
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deviceTypeSpecificInformation: Record<string, any>;
    _lastJSON: ThirdPartyDevice;

    get type(): ThirdPartyDeviceType {
        return this.deviceType;
    }
    set type(v: ThirdPartyDeviceType) {
        this.deviceType = v;
    }
    get deviceConfiguration(): ThirdPartyDeviceConfiguration {
        return DeviceTypeConfigurations[this.deviceType];
    }
    constructor(json: ThirdPartyDevice) {
        super(json);
        this.assignFromJSON(json);
        this.load().catch((e) => {
            console.warn(e);
        });
    }
    static async createNew(
        node_id: string,
        deviceType: string,
        deviceInfo: {
            lat: number;
            long: number;
            enclosureID: string;
            name: string;
            connectedZones?: string[];
            connectedSensors?: SensorSpecifier[];
        },
    ) {
        const { lat, long, enclosureID, name, connectedZones, connectedSensors } = deviceInfo;
        const thirdPartyDeviceModel = new ThirdPartyDevice({
            EnclosureID: enclosureID,
            node_id,
            deviceType: deviceType as ThirdPartyDeviceType,
            position: VertexInfo.utilities.createFromLatLng(lat, long),
            owner: SessionHandler.curUser.toMongoId(),
            name,
            connectedZones,
            connectedSensors,
        } as ConstructorParameters<typeof ThirdPartyDevice>[0]);
        return await APICommands.ThirdPartyDeviceAPI.addOne(thirdPartyDeviceModel).then(
            (r) => new ThirdPartyDeviceBase(r),
        );
    }
    async delete() {
        await APICommands.ThirdPartyDeviceAPI.deleteOne(this.id).then(() => {
            super.delete();
        });
    }
    async load() {
        const connectedZones = this.connectedZoneIDs;
        const promises = [];
        connectedZones.forEach((cz, i) => {
            promises.push(
                findEventually("zone", cz)
                    .then((r) => {
                        this.connectedZones[i] = r;
                        r.devices.push(this);
                    })
                    .catch((r) => {
                        console.warn(r);
                        console.warn(`Zone ${cz} not found or not loaded`);
                    }),
            );
        });
        await Promise.all(promises);
        await super.load();
    }

    async edit(args: Partial<ThirdPartyDevice>) {
        const json = await APICommands.ThirdPartyDeviceAPI.editOne(this.dbID, args);
        const newZoneObjectList = [];
        const promises = [];
        json.connectedZones.forEach((newZone, i) => {
            promises.push(
                findEventually("zone", newZone)
                    .then((r) => {
                        newZoneObjectList[i] = r;
                    })
                    .catch(() => {
                        console.warn(`Zone ${newZone} not found or not loaded`);
                    }),
            );
        });
        while (promises.length > 0) {
            await promises.pop();
        }
        this.connectedZones.forEach((z) => {
            if (z && !newZoneObjectList.includes(z)) {
                z.devices = z.devices.filter((d) => d !== this);
            }
        });
        // As load() does for the zones a device starts with: a newly connected zone lists this device too,
        // which is what zone graphing and data export read.
        newZoneObjectList.forEach((z) => {
            if (z && !z.devices.includes(this)) {
                z.devices.push(this);
            }
        });
        this.connectedZones = newZoneObjectList;
        this.lastRefreshed = new Date(Date.now());
        this.assignFromJSON(json);
        this.onChange.trigger({});
        return this;
    }
    checkForRecentData = true;

    assignFromJSON(json: ThirdPartyDevice): void {
        super.assignFromJSON(json);
        this._lastJSON = json;
        this.connectedZoneIDs = json.connectedZones || [];
        this.name = json.name || json.node_id || this.name;
        this.lat = json.position?.lat || this.lat;
        this.long = json.position?.lng || this.long;
        this.deviceType = json.deviceType;
        this.EnclosureID = json.EnclosureID || this.EnclosureID;
        //this.node_id = json.node_id;
        this.dbID = json.node_id;
        this.notes = json.notes ?? this.notes ?? "";
        this.adminNotes = json.adminNotes ?? this.adminNotes ?? "";
        this.externalLinks = json.externalLinks ?? this.externalLinks ?? {};
        if (json.deviceType) {
            this.type = json.deviceType;
        }
        this.singleValve = false;
        this.doubleValve = false;
        this.blockValve = false;
        if (json.connectedSensors && Array.isArray(json.connectedSensors)) {
            this.connectedSensors = json.connectedSensors.map((j) => {
                const newSensor = new IrrigationDeviceSensor(j);
                this.loadPromise.then(() => {
                    if (this.recentData[newSensor.dataType]) {
                        newSensor.lastDataPoint = this.recentData[newSensor.dataType];
                    }
                });
                return newSensor;
            });
        }
        try {
            if (json.warnings) {
                this.warnings = json.warnings.map((w) => new DeviceWarning(w));
            }
            this.warnings.sort((a, b) => {
                const aScore = a.warningLevel * 10000 + a.warningTypePriority;
                const bScore = b.warningLevel * 10000 + b.warningTypePriority;
                return bScore - aScore;
            });
        } catch (e) {
            console.warn(e);
        }
        this.deviceTypeSpecificInformation = json.deviceTypeSpecificInformation;

        // @ts-ignore
        this.sensoterraDev = this.type === "msense";
    }
}
